
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    ArrowLeft,
    ArrowRight,
    BadgeCheck,
    Check,
    CheckCircle2,
    ChevronRight,
    CircleAlert,
    Clock3,
    CreditCard,
    Headphones,
    Hotel,
    LoaderCircle,
    LockKeyhole,
    Mail,
    Shield,
    ShieldCheck,
    Sparkles,
} from "lucide-react";

const benefits = [
    {
        icon: ShieldCheck,
        title: "Secure Checkout",
        description:
            "Continue to the payment gateway to complete your transaction.",
    },
    {
        icon: CreditCard,
        title: "Trusted Payment Processing",
        description:
            "Payments are processed through Mastercard Payment Gateway Services.",
    },
    {
        icon: Headphones,
        title: "Booking Assistance",
        description:
            "Our support team is available if you need help with your booking.",
    },
];

export default function PaymentPage() {
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get("session");

    const [ready, setReady] = useState(false);
    const [accepted, setAccepted] = useState(false);
    const [status, setStatus] = useState("");
    const [launching, setLaunching] = useState(false);

    useEffect(() => {
        setReady(false);
        setStatus("");
        setLaunching(false);

        if (!sessionId) return;

        let active = true;

        window.errorCallback = (error) => {
            console.error("Payment gateway error:", error);

            if (active) {
                setLaunching(false);
                setStatus(
                    "The payment could not be completed. Please try again."
                );
            }
        };

        window.cancelCallback = () => {
            if (active) {
                setLaunching(false);
                setStatus(
                    "Payment was cancelled. You can try again when ready."
                );
            }
        };

        const script = document.createElement("script");

        script.src =
            "https://ap-gateway.mastercard.com/static/checkout/checkout.min.js";

        script.async = true;
        script.setAttribute("data-error", "errorCallback");
        script.setAttribute("data-cancel", "cancelCallback");

        script.onload = () => {
            if (active) {
                if (window.Checkout) {
                    setReady(true);
                } else {
                    setStatus(
                        "The payment gateway is unavailable. Please refresh and try again."
                    );
                }
            }
        };

        script.onerror = () => {
            if (active) {
                setStatus(
                    "Unable to load the payment gateway. Please refresh and try again."
                );
            }
        };

        document.body.appendChild(script);

        return () => {
            active = false;
            script.remove();
            delete window.errorCallback;
            delete window.cancelCallback;
        };
    }, [sessionId]);

    function startPayment() {
        if (
            !accepted ||
            !ready ||
            !sessionId ||
            !window.Checkout ||
            launching
        ) {
            return;
        }

        try {
            setStatus("");
            setLaunching(true);

            window.Checkout.configure({
                session: {
                    id: sessionId,
                },
            });

            window.Checkout.showPaymentPage();
        } catch (error) {
            console.error("Checkout launch failed:", error);

            setLaunching(false);
            setStatus(
                "Unable to open secure checkout. Please try again."
            );
        }
    }

    const canProceed =
        Boolean(sessionId) && ready && accepted && !launching;

    return (
        <div className="min-h-screen overflow-hidden bg-[#F5FAF7] text-[#17324D]">
            {/* HERO */}
            <section className="relative isolate overflow-hidden bg-[#064E3B] pt-28 text-white sm:pt-32">
                {/* Decorative background */}
                <div className="pointer-events-none absolute -right-20 -top-32 h-[400px] w-[400px] rounded-full border border-white/10" />
                <div className="pointer-events-none absolute -right-8 -top-20 h-[300px] w-[300px] rounded-full border border-white/10" />
                <div className="pointer-events-none absolute -bottom-32 left-[30%] h-[300px] w-[300px] rounded-full bg-[#168B43]/25 blur-3xl" />

                <div className="relative mx-auto max-w-[1536px] px-4 pb-16 pt-4 sm:px-6 sm:pb-20 lg:px-8">
                    {/* Breadcrumb */}
                    <nav
                        aria-label="Breadcrumb"
                        className="flex flex-wrap items-center gap-2 text-xs font-semibold text-white/65"
                    >
                        <Link
                            to="/"
                            className="transition hover:text-white"
                        >
                            Home
                        </Link>

                        <ChevronRight size={14} />

                        <span className="text-[#B6F2CB]">
                            Secure Payment
                        </span>
                    </nav>

                    <div className="mt-10 grid items-end gap-8 lg:grid-cols-[1fr_auto]">
                        <div className="max-w-[780px]">
                            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#B6F2CB]">
                                <LockKeyhole size={15} />
                                Secure Hotel Booking
                            </div>

                            <h1 className="mt-6 text-[40px] font-extrabold leading-[1.12] tracking-tight text-white sm:text-[56px] lg:text-[64px]">
                                Complete Your
                                <br />
                                <span className="text-[#A5E8B8]">
                                    Payment.
                                </span>
                            </h1>

                            <p className="mt-5 max-w-[610px] text-[14px] leading-8 text-white/75 sm:text-base">
                                You're just one step away from completing
                                your booking. Review the booking policies
                                and continue to our payment gateway.
                            </p>
                        </div>

                        <div className="hidden items-center gap-4 rounded-2xl border border-white/15 bg-white/10 p-5 backdrop-blur-sm lg:flex">
                            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#168B43] text-white">
                                <ShieldCheck size={28} />
                            </div>

                            <div>
                                <p className="text-sm font-extrabold text-white">
                                    Secure Checkout
                                </p>

                                <p className="mt-1 text-xs text-white/65">
                                    Mastercard Payment Gateway
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* MAIN CONTENT */}
            <main className="mx-auto max-w-[1536px] px-4 pb-20 pt-10 sm:px-6 sm:pt-14 lg:px-8">
                {/* Back link */}
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-[13px] font-bold text-[#64748B] transition hover:text-[#168B43]"
                >
                    <ArrowLeft size={16} />
                    Back to Home
                </Link>

                <div className="mt-7 grid items-start gap-7 lg:grid-cols-[minmax(0,1fr)_370px]">
                    {/* LEFT: PAYMENT CARD */}
                    <section className="overflow-hidden rounded-[24px] border border-[#DCEAE1] bg-white shadow-[0_15px_50px_rgba(6,78,59,0.05)]">
                        {/* Card header */}
                        <div className="border-b border-[#E8F0EA] px-6 py-6 sm:px-9">
                            <div className="flex flex-wrap items-center justify-between gap-4">
                                <div className="flex items-center gap-4">
                                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#E8F7EE] text-[#168B43]">
                                        <CreditCard size={24} />
                                    </div>

                                    <div>
                                        <p className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#168B43]">
                                            Final Booking Step
                                        </p>

                                        <h2 className="mt-1 text-xl font-extrabold tracking-tight text-[#17324D] sm:text-[24px]">
                                            Payment Authorization
                                        </h2>
                                    </div>
                                </div>

                                <span className="inline-flex items-center gap-2 rounded-full bg-[#E8F7EE] px-3 py-2 text-[11px] font-extrabold text-[#168B43]">
                                    <LockKeyhole size={13} />
                                    Secure Gateway
                                </span>
                            </div>
                        </div>

                        <div className="p-6 sm:p-9 lg:p-10">
                            {/* STEP INDICATOR */}
                            <div className="flex items-center gap-3 sm:gap-5">
                                <div className="flex min-w-0 items-center gap-3">
                                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#168B43] text-white shadow-[0_5px_15px_rgba(22,139,67,0.2)]">
                                        <Check size={18} strokeWidth={3} />
                                    </span>

                                    <div>
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">
                                            Step 01
                                        </p>

                                        <p className="text-[12px] font-extrabold text-[#17324D] sm:text-sm">
                                            Review Policies
                                        </p>
                                    </div>
                                </div>

                                <div className="h-px min-w-4 flex-1 bg-[#DCEAE1]" />

                                <div className="flex min-w-0 items-center gap-3">
                                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-[#B9DEC9] bg-[#F5FAF7] text-[#168B43]">
                                        <CreditCard size={17} />
                                    </span>

                                    <div>
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">
                                            Step 02
                                        </p>

                                        <p className="text-[12px] font-extrabold text-[#17324D] sm:text-sm">
                                            Secure Payment
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* INTRO */}
                            <div className="mt-10">
                                <p className="text-[11px] font-extrabold uppercase tracking-[0.17em] text-[#168B43]">
                                    Almost There
                                </p>

                                <h3 className="mt-3 text-[28px] font-extrabold leading-tight tracking-tight text-[#17324D] sm:text-[36px]">
                                    Ready To Confirm{" "}
                                    <span className="text-[#168B43]">
                                        Your Stay?
                                    </span>
                                </h3>

                                <p className="mt-4 max-w-[620px] text-[14px] leading-8 text-[#718096]">
                                    Please review and accept our booking
                                    policies before proceeding. Your payment
                                    will be completed through Mastercard
                                    Payment Gateway Services.
                                </p>
                            </div>

                            {/* POLICY AGREEMENT */}
                            <div
                                className={`mt-8 rounded-[18px] border p-5 transition-all duration-200 sm:p-6 ${accepted
                                    ? "border-[#B9DEC9] bg-[#F0FAF3]"
                                    : "border-[#DCEAE1] bg-[#F8FBF9]"
                                    }`}
                            >
                                <div className="flex items-start gap-4">
                                    <input
                                        id="payment-consent"
                                        type="checkbox"
                                        checked={accepted}
                                        onChange={(event) =>
                                            setAccepted(event.target.checked)
                                        }
                                        className="mt-1 h-5 w-5 shrink-0 cursor-pointer accent-[#168B43]"
                                    />

                                    <div className="min-w-0 flex-1">
                                        <label
                                            htmlFor="payment-consent"
                                            className="cursor-pointer text-[14px] font-extrabold text-[#17324D]"
                                        >
                                            I agree to the booking policies
                                        </label>

                                        <p className="mt-2 text-[13px] leading-7 text-[#718096]">
                                            I confirm that I have read and agree
                                            to the{" "}
                                            <Link
                                                to="/terms-conditions"
                                                className="font-extrabold text-[#168B43] underline underline-offset-4 hover:text-[#064E3B]"
                                            >
                                                Terms &amp; Conditions
                                            </Link>{" "}
                                            and{" "}
                                            <Link
                                                to="/privacy-policy"
                                                className="font-extrabold text-[#168B43] underline underline-offset-4 hover:text-[#064E3B]"
                                            >
                                                Privacy Policy
                                            </Link>
                                            .
                                        </p>
                                    </div>

                                    {accepted && (
                                        <CheckCircle2
                                            size={22}
                                            className="hidden shrink-0 text-[#168B43] sm:block"
                                        />
                                    )}
                                </div>
                            </div>

                            {/* PAYMENT STATUS */}
                            <div className="mt-6" aria-live="polite">
                                {!sessionId ? (
                                    <div
                                        role="alert"
                                        className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4"
                                    >
                                        <CircleAlert
                                            size={20}
                                            className="mt-0.5 shrink-0 text-red-600"
                                        />

                                        <div>
                                            <p className="text-sm font-extrabold text-red-800">
                                                Payment Session Missing
                                            </p>

                                            <p className="mt-1 text-xs leading-6 text-red-700">
                                                Your payment session was not found.
                                                Please return to your booking and
                                                try again.
                                            </p>
                                        </div>
                                    </div>
                                ) : status ? (
                                    <div
                                        role="alert"
                                        className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4"
                                    >
                                        <CircleAlert
                                            size={20}
                                            className="mt-0.5 shrink-0 text-amber-700"
                                        />

                                        <p className="text-[13px] leading-6 text-amber-900">
                                            {status}
                                        </p>
                                    </div>
                                ) : ready ? (
                                    <div className="flex items-start gap-3 rounded-xl border border-[#B9DEC9] bg-[#F0FAF3] p-4">
                                        <CheckCircle2
                                            size={20}
                                            className="mt-0.5 shrink-0 text-[#168B43]"
                                        />

                                        <div>
                                            <p className="text-sm font-extrabold text-[#064E3B]">
                                                Secure Checkout Ready
                                            </p>

                                            <p className="mt-1 text-xs leading-6 text-[#47715A]">
                                                Your payment gateway is ready.
                                                You can continue when you're ready.
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-3 rounded-xl border border-[#DCEAE1] bg-[#F8FBF9] p-4">
                                        <LoaderCircle
                                            size={20}
                                            className="shrink-0 animate-spin text-[#168B43]"
                                        />

                                        <p className="text-[13px] font-semibold text-[#64748B]">
                                            Preparing secure checkout...
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* PAYMENT BUTTON */}
                            <button
                                type="button"
                                onClick={startPayment}
                                disabled={!canProceed}
                                className="group mt-7 flex w-full items-center justify-center gap-3 rounded-xl bg-[#168B43] px-6 py-[18px] text-sm font-extrabold text-white shadow-[0_10px_25px_rgba(22,139,67,0.17)] transition-all duration-200 hover:bg-[#087D37] hover:shadow-[0_12px_30px_rgba(22,139,67,0.23)] disabled:cursor-not-allowed disabled:bg-[#AFC7B8] disabled:shadow-none"
                            >
                                {launching ? (
                                    <>
                                        <LoaderCircle
                                            size={18}
                                            className="animate-spin"
                                        />
                                        Opening Secure Checkout...
                                    </>
                                ) : (
                                    <>
                                        <LockKeyhole size={18} />
                                        Proceed To Secure Payment
                                        <ArrowRight
                                            size={18}
                                            className="transition-transform group-hover:translate-x-1"
                                        />
                                    </>
                                )}
                            </button>

                            <p className="mt-4 text-center text-[12px] leading-6 text-[#94A3B8]">
                                You will continue to the payment gateway
                                to complete your transaction.
                            </p>

                            {/* TRUST STRIP */}
                            <div className="mt-8 grid gap-3 border-t border-[#E8F0EA] pt-7 sm:grid-cols-3">
                                {[
                                    {
                                        icon: ShieldCheck,
                                        label: "Secure Checkout",
                                    },
                                    {
                                        icon: CreditCard,
                                        label: "Mastercard Gateway",
                                    },
                                    {
                                        icon: Clock3,
                                        label: "Final Step",
                                    },
                                ].map(({ icon: Icon, label }) => (
                                    <div
                                        key={label}
                                        className="flex items-center justify-center gap-2 rounded-xl bg-[#F8FBF9] px-3 py-3"
                                    >
                                        <Icon
                                            size={16}
                                            className="shrink-0 text-[#168B43]"
                                        />

                                        <span className="text-[11px] font-bold text-[#64748B]">
                                            {label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* RIGHT: SECURITY SIDEBAR */}
                    <aside className="space-y-5">
                        <div className="relative overflow-hidden rounded-[24px] bg-[#064E3B] p-7 text-white shadow-[0_15px_45px_rgba(6,78,59,0.1)] sm:p-8">
                            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full border border-white/10" />
                            <div className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 rounded-full border border-white/10" />

                            <div className="relative">
                                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 text-[#A5E8B8]">
                                    <ShieldCheck size={28} />
                                </div>

                                <p className="mt-7 text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#B6F2CB]">
                                    Travel With Confidence
                                </p>

                                <h2 className="mt-3 text-[28px] font-extrabold leading-tight tracking-tight text-white">
                                    Secure Payment.
                                    <br />
                                    <span className="text-[#A5E8B8]">
                                        Peace Of Mind.
                                    </span>
                                </h2>

                                <p className="mt-4 text-[13px] leading-7 text-white/75">
                                    Complete your booking through our
                                    integrated payment gateway.
                                </p>

                                <div className="mt-7 space-y-6 border-t border-white/15 pt-7">
                                    {benefits.map(
                                        ({ icon: Icon, title, description }) => (
                                            <div
                                                key={title}
                                                className="flex items-start gap-4"
                                            >
                                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 text-[#A5E8B8]">
                                                    <Icon size={20} />
                                                </div>

                                                <div>
                                                    <h3 className="text-[13px] font-extrabold text-white">
                                                        {title}
                                                    </h3>

                                                    <p className="mt-1 text-[12px] leading-6 text-white/65">
                                                        {description}
                                                    </p>
                                                </div>
                                            </div>
                                        )
                                    )}
                                </div>

                                <div className="mt-8 flex items-start gap-3 rounded-xl border border-white/15 bg-white/10 p-4">
                                    <LockKeyhole
                                        size={18}
                                        className="mt-0.5 shrink-0 text-[#A5E8B8]"
                                    />

                                    <p className="text-[12px] leading-6 text-white/80">
                                        Complete payment only through the
                                        official secure checkout page.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* HELP CARD */}
                        <div className="rounded-[22px] border border-[#DCEAE1] bg-white p-6">
                            <div className="flex items-start gap-4">
                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#E8F7EE] text-[#168B43]">
                                    <Headphones size={23} />
                                </div>

                                <div>
                                    <h3 className="text-[16px] font-extrabold text-[#17324D]">
                                        Need Help?
                                    </h3>

                                    <p className="mt-2 text-[12px] leading-6 text-[#718096]">
                                        Have a question about your payment
                                        or booking? Our team can assist.
                                    </p>
                                </div>
                            </div>

                            <a
                                href="mailto:info@southtravels.com"
                                className="mt-5 flex items-center justify-center gap-2 rounded-xl border border-[#B9DEC9] bg-[#F5FAF7] px-4 py-3.5 text-[12px] font-extrabold text-[#168B43] transition hover:border-[#168B43] hover:bg-[#E8F7EE]"
                            >
                                <Mail size={16} />
                                info@southtravels.com
                                <ArrowRight size={15} />
                            </a>
                        </div>
                    </aside>
                </div>

                {/* BOTTOM NOTE */}
                <div className="mt-10 flex flex-col items-center justify-center gap-3 text-center sm:flex-row">
                    <ShieldCheck
                        size={18}
                        className="text-[#168B43]"
                    />

                    <p className="text-[12px] leading-6 text-[#718096]">
                        Payments are processed through Mastercard
                        Payment Gateway Services.
                    </p>
                </div>
            </main>
        </div>
    );
}
